import { checkHHStatus, getAtsConnectionStatus, getAtsIntegrations } from '../services/api';

export type SearchSourceConnectionStatus =
  | 'connected'
  | 'auth-required'
  | 'ats-required'
  | 'unavailable';

type HhStatusResponse = {
  status?: string;
};

type AtsIntegrationConfiguration = {
  configured?: boolean;
};

type AtsIntegrationsResponse = {
  configurations?: Record<string, AtsIntegrationConfiguration | null | undefined>;
};

const COMPANY_ATS_REQUIRED_SOURCES = new Set(['hh', 'avito']);

export const normalizeSearchSource = (source?: string): string =>
  String(source || 'hh').trim().toLowerCase() || 'hh';

export const hasAnyConfiguredAts = (integrationsResponse?: AtsIntegrationsResponse): boolean => {
  const configurations = integrationsResponse?.configurations || {};
  return Object.values(configurations).some(configuration => !!configuration?.configured);
};

const resolveAtsStatus = async (source: string): Promise<SearchSourceConnectionStatus> => {
  if (COMPANY_ATS_REQUIRED_SOURCES.has(source)) {
    const integrations = await getAtsIntegrations();
    return hasAnyConfiguredAts(integrations) ? 'connected' : 'ats-required';
  }

  const connection = await getAtsConnectionStatus(source);
  return connection?.configured ? 'connected' : 'ats-required';
};

const isNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const response = (error as { response?: { status?: unknown } }).response;
  return response?.status === 404;
};

export const resolveSearchSourceConnectionStatus = async (
  source?: string,
): Promise<SearchSourceConnectionStatus> => {
  const normalizedSource = normalizeSearchSource(source);

  if (normalizedSource === 'hh') {
    let hhStatus: HhStatusResponse;
    try {
      hhStatus = await checkHHStatus();
    } catch (error: unknown) {
      if (isNotFoundError(error)) {
        return 'auth-required';
      }
      throw error;
    }

    if (hhStatus?.status === 'unauthorized') {
      return 'auth-required';
    }
    if (hhStatus?.status !== 'authorized') {
      return 'unavailable';
    }
  }

  try {
    return await resolveAtsStatus(normalizedSource);
  } catch (error: unknown) {
    if (isNotFoundError(error)) {
      return 'ats-required';
    }
    throw error;
  }
};
